/**
 * Option 1b bundle reporting allocation:
 * - allocatedPrice: proportional share of bundle base (dishPrice), weighted by dish + locked preselections
 * - allocatedPriceWithVariants: allocatedPrice + per-line extras (step adjustment + optional modifiers)
 *
 * Locked preselection charges are read from menu options (cart stores charge "0" for locked lines).
 */

export const parseBundleMoney = (
  value: number | string | null | undefined
): number => {
  const parsed =
    typeof value === "string" ? parseFloat(value) : Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const roundAllocatedMoney = (value: number): number =>
  Math.round(value * 100) / 100;

export const getBundleStepsFromConfig = (bundleConfig: any): any[] => {
  if (!bundleConfig) {
    return [];
  }
  if (Array.isArray(bundleConfig.Steps)) {
    return bundleConfig.Steps;
  }
  if (Array.isArray(bundleConfig.steps)) {
    return bundleConfig.steps;
  }
  return [];
};

export const findMatchingBundleStepSubItem = (
  dish: any,
  bundleStep: any | null | undefined
): any | null => {
  if (!bundleStep?.subItems || !Array.isArray(bundleStep.subItems)) {
    return null;
  }

  let matchingSubItem = bundleStep.subItems.find((subItem: any) => {
    if (!subItem?.id) {
      return false;
    }
    if (subItem.type === "item") {
      return subItem.id === dish?.id;
    }
    if (!subItem.type && subItem.id === dish?.id) {
      return true;
    }
    return false;
  });

  if (!matchingSubItem && dish?.categoryId) {
    matchingSubItem = bundleStep.subItems.find((subItem: any) => {
      if (!subItem?.id) {
        return false;
      }
      if (subItem.type === "category" || subItem.type === "Category") {
        return subItem.id === dish.categoryId;
      }
      if (!subItem.type && subItem.id === dish.categoryId) {
        return true;
      }
      return false;
    });
  }

  return matchingSubItem || null;
};

const getVariantCharge = (variant: any): number => {
  if (!variant?.extraCharge || variant?.charge == null) {
    return 0;
  }
  return parseBundleMoney(variant.charge);
};

/** Locked lines persisted on cart — usually charge 0 (baked into bundle base). */
export const getLockedVariantChargesTotal = (variants: any[] = []): number =>
  (variants || []).reduce(
    (sum, variant) =>
      variant?.isLocked ? sum + getVariantCharge(variant) : sum,
    0
  );

/** Optional (non-locked) modifier charges — added on top of bundle base on parent total. */
export const getOptionalVariantChargesTotal = (variants: any[] = []): number =>
  (variants || []).reduce(
    (sum, variant) =>
      !variant?.isLocked ? sum + getVariantCharge(variant) : sum,
    0
  );

/**
 * Menu charge for bundle-config locked preselections (e.g. Large +$7.50).
 * Cart locked lines intentionally store charge "0".
 */
export const getLockedPreselectionValueFromBundleStep = (
  dish: any,
  bundleStep: any | null | undefined
): number => {
  if (!dish?.variants || !Array.isArray(dish.variants)) {
    return 0;
  }

  const matchingSubItem = findMatchingBundleStepSubItem(dish, bundleStep);
  const lockedOptions = matchingSubItem?.modifierRules?.lockedOptions;
  if (!Array.isArray(lockedOptions)) {
    return 0;
  }

  return lockedOptions.reduce((sum: number, rule: any) => {
    if (!rule?.variantId || (rule?.locked ?? true) !== true) {
      return sum;
    }
    const variant = dish.variants.find(
      (v: any) => v?.id === rule.variantId || v?.variantId === rule.variantId
    );
    if (!variant) {
      return sum;
    }
    if (!rule.optionName) {
      return sum;
    }
    const option = (variant.options || []).find(
      (opt: any) => opt?.name === rule.optionName
    );
    if (!option?.extraCharge) {
      return sum;
    }
    return sum + parseBundleMoney(option.charge);
  }, 0);
};

export const parseStepPriceAdjustment = (
  priceAdjustment: number | string | null | undefined
): number => {
  if (priceAdjustment == null || priceAdjustment === "") {
    return 0;
  }
  return parseBundleMoney(priceAdjustment);
};

export const getLockedPreselectionWeight = (
  dish: any,
  variants: any[] = [],
  bundleStep?: any | null
): number => {
  const fromMenu = getLockedPreselectionValueFromBundleStep(dish, bundleStep);
  if (fromMenu > 0) {
    return fromMenu;
  }
  return getLockedVariantChargesTotal(variants);
};

/** Weight for splitting bundle base across steps. */
export const getBundleStepBaseWeight = (
  baseDishPrice: number,
  variants: any[] = [],
  dish?: any,
  bundleStep?: any | null
): number =>
  Math.max(0, baseDishPrice) +
  (dish != null
    ? getLockedPreselectionWeight(dish, variants, bundleStep)
    : getLockedVariantChargesTotal(variants));

/** Extras on this step that increase parent dishPriceWithVariants beyond bundle base. */
export const getBundleStepLineExtras = (
  priceAdjustment: number | string | null | undefined,
  variants: any[] = []
): number =>
  parseStepPriceAdjustment(priceAdjustment) +
  getOptionalVariantChargesTotal(variants);

export const getNestedSplitSelectionsBaseWeight = (dish: any): number => {
  const nestedSteps = getBundleStepsFromConfig(dish?.bundleConfig);
  const nestedSelections = Array.isArray(dish?.bundleSelections)
    ? dish.bundleSelections
    : [];
  return nestedSelections.reduce((sum: number, nestedSelection: any) => {
    if (!nestedSelection?.selectedItem?.dish) {
      return sum;
    }
    const nestedDish = nestedSelection.selectedItem.dish;
    const nestedVariants = nestedSelection.selectedItem.variants || [];
    const nestedStep =
      nestedSteps.find((s: any) => s?.id === nestedSelection?.stepId) || null;
    return (
      sum +
      getBundleStepBaseWeight(
        parseBundleMoney(nestedDish.dishPrice),
        nestedVariants,
        nestedDish,
        nestedStep
      )
    );
  }, 0);
};

export const resolveBundleStepBaseWeight = (
  dish: any,
  variants: any[] = [],
  bundleStep?: any | null
): number => {
  const isNestedSplitBundle =
    dish?.isBundle === true && dish?.bundleConfig?.isSplit === true;
  if (isNestedSplitBundle && Array.isArray(dish?.bundleSelections)) {
    const nestedWeight = getNestedSplitSelectionsBaseWeight(dish);
    const outerWeight = getBundleStepBaseWeight(
      parseBundleMoney(dish?.dishPrice),
      variants,
      dish,
      bundleStep
    );
    return nestedWeight + outerWeight;
  }
  return getBundleStepBaseWeight(
    parseBundleMoney(dish?.dishPrice),
    variants,
    dish,
    bundleStep
  );
};

export const allocatePoolByWeight = (
  pool: number,
  weight: number,
  sumWeights: number,
  fallbackCount: number
): number => {
  if (!Number.isFinite(pool) || pool < 0) {
    return 0;
  }
  if (sumWeights > 0 && Number.isFinite(weight)) {
    return roundAllocatedMoney((pool * weight) / sumWeights);
  }
  return roundAllocatedMoney(pool / Math.max(1, fallbackCount));
};

export const computeStepAllocatedPrices = (params: {
  bundleBasePrice: number;
  baseWeight: number;
  sumBaseWeights: number;
  selectedStepCount: number;
  lineExtras: number;
}): { allocatedPrice: number; allocatedPriceWithVariants: number } => {
  const allocatedPrice = allocatePoolByWeight(
    params.bundleBasePrice,
    params.baseWeight,
    params.sumBaseWeights,
    params.selectedStepCount
  );
  const allocatedPriceWithVariants = roundAllocatedMoney(
    allocatedPrice + (params.lineExtras || 0)
  );
  return { allocatedPrice, allocatedPriceWithVariants };
};
